import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listLocalRunners, type LocalRunner } from '@api/localRunners';
import useAuth from '@contexts/useAuth';

const MY_LOCAL_RUNNERS_KEY = ['local-runners', 'mine'] as const;

const POLL_MS = 10_000;

/**
 * How recent a runner's heartbeat must be to treat it as genuinely connected.
 * The runner beats every 10s; the control plane only flips a dead runner's
 * status to OFFLINE on a slow (~10 min) sweep, so a killed runner lingers as
 * READY in the list. We must not prompt against that ghost — check the
 * heartbeat directly. Window covers one beat interval + one poll gap + margin.
 */
const HEARTBEAT_FRESH_MS = 30_000;

function isHeartbeatFresh(lastHeartbeat: string | null, nowMs: number): boolean {
  if (!lastHeartbeat) return false;
  const beat = Date.parse(lastHeartbeat);
  if (Number.isNaN(beat)) return false;
  return nowMs - beat <= HEARTBEAT_FRESH_MS;
}

/**
 * True when the caller has at least one runner that is idle (READY) AND
 * heartbeating right now — i.e. one the allocator could actually claim.
 */
export function hasLiveReadyRunner(
  runners: readonly LocalRunner[] | undefined,
  nowMs: number = Date.now(),
): boolean {
  return (runners ?? []).some(
    (r) => r.status === 'READY' && isHeartbeatFresh(r.lastHeartbeat, nowMs),
  );
}

function coversApps(
  installed: readonly string[] | undefined,
  needed: readonly string[],
): boolean {
  if (needed.length === 0) return true;
  const have = new Set((installed ?? []).map((a) => a.toLowerCase()));
  return needed.every((a) => have.has(a.toLowerCase()));
}

/**
 * True when the caller has a live READY runner that also advertises every app
 * in {@code requiredApps} — i.e. one the allocator could actually claim for
 * this node. The allocator applies the same READY + fresh-heartbeat + required-
 * apps filter, so a desktop-agent node whose app no online runner has must not
 * surface the "Allow" prompt (accepting could never convert to a claim, which
 * otherwise re-parks and re-prompts forever). An empty requirement (headless
 * agent) matches any live runner.
 */
export function hasClaimableRunner(
  runners: readonly LocalRunner[] | undefined,
  requiredApps: readonly string[] = [],
  nowMs: number = Date.now(),
): boolean {
  return (runners ?? []).some(
    (r) =>
      r.status === 'READY' &&
      isHeartbeatFresh(r.lastHeartbeat, nowMs) &&
      coversApps(r.installedApps, requiredApps),
  );
}

/**
 * The caller's own local runners with live status. Only fetched (and polled)
 * while {@code enabled} so it costs nothing outside a runner-claim wait; the
 * poll lets a runner connected mid-wait surface the "Allow" prompt without a
 * page refresh.
 */
export function useMyLocalRunners(
  enabled: boolean,
): UseQueryResult<LocalRunner[], Error> {
  const { user } = useAuth();
  const token = user?.token;

  return useQuery<LocalRunner[], Error>({
    queryKey: [...MY_LOCAL_RUNNERS_KEY, user?.id],
    queryFn: () => listLocalRunners(),
    enabled: enabled && Boolean(token),
    staleTime: 5_000,
    refetchInterval: enabled ? POLL_MS : false,
    // Re-render on every poll (even when the payload is byte-identical) so the
    // heartbeat-freshness check re-evaluates against the advancing clock and a
    // runner that silently stopped beating drops out of the prompt.
    structuralSharing: false,
    retry: 1,
  });
}
